import { useMemo } from 'react';
import { formatAddress } from '../lib/blockchain';
import { useStaking } from '../hooks/useStaking';

const getWalletAddress = (props) =>
  props?.account ||
  props?.wallet?.account ||
  props?.wallet?.address ||
  props?.walletState?.account ||
  props?.walletState?.address ||
  props?.metaMask?.account ||
  props?.metaMask?.address ||
  '';

const displayValue = (value, fallback = '—') => {
  if (value === undefined || value === null || value === '') return fallback;
  return `${value}`;
};

export default function Staking(props) {
  const account = getWalletAddress(props);
  const { nodeInfo, leaderboard, loading, error, refetch } = useStaking(account);

  const metrics = useMemo(
    () => [
      {
        label: 'Delegated stake',
        value: displayValue(nodeInfo?.stake, '0'),
        helper: 'Current amount reported by the staking info endpoint.',
      },
      {
        label: 'Pending rewards',
        value: displayValue(nodeInfo?.rewards, '0'),
        helper: 'Rewards are informational only in the current frontend.',
      },
      {
        label: 'Node status',
        value: displayValue(nodeInfo?.status, account ? 'Monitoring' : 'Disconnected'),
        helper: 'Live mutation actions are not exposed by the backend yet.',
      },
      {
        label: 'Estimated APY',
        value: displayValue(nodeInfo?.apy, 'Variable'),
        helper: 'Displayed as reported by the chain or a safe fallback.',
      },
    ],
    [account, nodeInfo]
  );

  return (
    <div className="space-y-6">
      <section className="glass p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">Staking</p>
            <h1 className="mt-2 text-3xl font-semibold text-white">Validator and reward overview</h1>
            <p className="mt-2 text-slate-300">
              Track your staking position and compare public validator data exposed by the backend.
            </p>
          </div>

          <button
            type="button"
            className="rounded-full border border-white/10 px-4 py-2 text-sm text-white hover:bg-white/5"
            onClick={() => refetch()}
          >
            Refresh staking data
          </button>
        </div>
      </section>

      {error ? (
        <section className="glass border border-rose-500/30 p-4 text-rose-200">{error}</section>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <article key={metric.label} className="glass p-5">
            <p className="text-sm text-slate-400">{metric.label}</p>
            <h2 className="mt-2 text-2xl font-semibold text-white">{loading ? 'Loading…' : metric.value}</h2>
            <p className="mt-1 text-sm text-slate-400">{metric.helper}</p>
          </article>
        ))}
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <article className="glass p-6">
          <h2 className="text-xl font-semibold text-white">Your node snapshot</h2>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Wallet</span>
              <span className="text-white">{account ? formatAddress(account) : 'Not connected'}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Display name</span>
              <span className="text-white">{displayValue(nodeInfo?.name, 'Wallet not registered')}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Status</span>
              <span className="text-white">{displayValue(nodeInfo?.status)}</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
              <span className="text-slate-400">Uptime</span>
              <span className="text-white">{displayValue(nodeInfo?.uptime)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-400">Commission</span>
              <span className="text-white">{displayValue(nodeInfo?.commission)}</span>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-100">
            Claim, unstake and validator registration flows are not exposed as frontend actions because
            the current backend only provides read-side staking information.
          </div>
        </article>

        <article className="glass p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Leaderboard</h2>
            <span className="text-sm text-slate-400">{leaderboard.length} entries</span>
          </div>

          {loading ? (
            <p className="mt-4 text-slate-300">Loading validators…</p>
          ) : leaderboard.length ? (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-slate-400">
                  <tr>
                    <th className="pb-3 pr-4 font-medium">#</th>
                    <th className="pb-3 pr-4 font-medium">Validator</th>
                    <th className="pb-3 pr-4 font-medium">Stake</th>
                    <th className="pb-3 pr-4 font-medium">Rewards</th>
                    <th className="pb-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((entry) => (
                    <tr key={`${entry.address}-${entry.rank}`} className="border-t border-white/5">
                      <td className="py-3 pr-4 text-white">{entry.rank}</td>
                      <td className="py-3 pr-4">
                        <div>
                          <p className="text-white">{entry.name}</p>
                          <p className="text-xs text-slate-400">
                            {entry.address && entry.address !== '—'
                              ? formatAddress(entry.address)
                              : 'Address unavailable'}
                          </p>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-slate-300">{displayValue(entry.stake, '0')}</td>
                      <td className="py-3 pr-4 text-slate-300">{displayValue(entry.rewards, '0')}</td>
                      <td className="py-3 text-slate-300">{displayValue(entry.status, 'Active')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-slate-300">
              The staking endpoint is live, but it did not expose a leaderboard array in the current response.
            </p>
          )}
        </article>
      </section>
    </div>
  );
}